import { Link } from "react-router-dom";
import { ChevronLeft, Share2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { FavoriteButton } from "@/components/favorites/FavoriteButton";
import { LocaleSwitcher } from "@/components/common/LocaleSwitcher";
import { cn } from "@/lib/utils";

interface PublicProducerHeaderProps {
  producerId: string;
  producerSlug?: string | null;
  producerName: string;
  placeName?: string | null;
  backTo?: string;
  backLabel?: string;
  isCompact: boolean;
  onShare: () => void;
}

export function PublicProducerHeader({
  producerId,
  producerSlug,
  producerName,
  placeName,
  backTo = "/explore",
  backLabel = "Till Utforska",
  isCompact,
  onShare,
}: PublicProducerHeaderProps) {
  return (
    <header
      className={cn(
        "sticky top-0 z-50 w-full bg-background/80 backdrop-blur-md transition-shadow duration-200",
        isCompact && "shadow-sm border-b border-border/40"
      )}
    >
      <div className="container flex h-12 items-center gap-2">
        {/* Left: Back */}
        <Button
          variant="ghost"
          size="sm"
          className="gap-1 text-muted-foreground hover:text-foreground -ml-2 shrink-0"
          asChild
        >
          <Link to={backTo}>
            <ChevronLeft className="h-4 w-4" />
            <span className="text-sm">{backLabel}</span>
          </Link>
        </Button>

        {/* Center: compact title */}
        <div className="flex-1 min-w-0 text-center">
          <div
            className={cn(
              "transition-opacity duration-200",
              isCompact ? "opacity-100" : "opacity-0 pointer-events-none"
            )}
          >
            <p className="text-sm font-medium text-foreground truncate leading-tight">
              {producerName}
            </p>
            {placeName && (
              <p className="text-[11px] text-muted-foreground truncate leading-tight">
                {placeName}
              </p>
            )}
          </div>
        </div>

        {/* Right: LocaleSwitcher + Favorite + Share */}
        <div className="flex items-center gap-0.5 shrink-0">
          <LocaleSwitcher density="compact" />
          <FavoriteButton
            kind="producer"
            entityId={producerId}
            slug={producerSlug || undefined}
            title={producerName}
            size="sm"
          />
          <Button
            variant="ghost"
            size="icon"
            className="h-9 w-9 rounded-full text-muted-foreground hover:text-foreground"
            onClick={onShare}
            aria-label={`Dela ${producerName}`}
          >
            <Share2 className="h-4 w-4" />
          </Button>
        </div>
      </div>
    </header>
  );
}
